import { prefersReducedMotion } from '@scripts/lenis';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// スクロールで下からふわっと現れる汎用リビール。マークアップ側に data 属性を
// 付けるだけで効く (nav-panels の演出を属性駆動へ一般化したもの)。
//   [data-reveal]           … 単体要素。下から (y + autoAlpha)
//   [data-reveal="fade"]    … opacity のみ (大きな画像など y 移動が重い要素)
//   [data-reveal-group]     … コンテナ。直下の子を、画面に入った行ごとに
//                             stagger で順に (リスト向け)。
//                             data-reveal-stagger="0.1" で間隔 (秒) を個別上書き
//   [data-reveal-group="together"]
//                           … コンテナ自体を 1 つの ScrollTrigger にし、そこが
//                             画面に入ったら直下の子をまとめて stagger 表示。
//                             背の高い画像群でも「群が見えたら全部出す」向け
//   [data-reveal-group="sequence"]
//                           … コンテナを 1 つの timeline にし、直下の子を上から
//                             順番に表示 (見出し→本文→…)。子がさらに
//                             data-reveal-group なら、その子孫を stagger して
//                             1 コマ分として差し込む。全要素が同時に画面内でも
//                             必ず宣言順に登場する。子に data-reveal-lead="0.3"
//                             を付けると、そのコマだけ既定位置より早く始まる
//   [data-reveal-fade]      … グループに付けると y 移動を省き opacity のみで
//                             登場 (単体の data-reveal="fade" のグループ版)
//
// data-reveal-start で ScrollTrigger の開始位置を個別上書きできる (単体/グループ
// 共通)。例: "center bottom" = 要素の中央がビューポート下端に来たら表示。
//
// 初期の非表示は global.css の `html.js [data-reveal] { opacity:0 }` が先に
// 伏せており、ちらつきを防ぐ。ここで opacity を 1 へ戻すことで登場する。
// clearProps は transform のみ (opacity を消すと CSS の 0 に戻ってしまうため
// インラインの opacity:1 は残す。transform は sticky 要素の含みブロックへの
// 影響を避けるため消す)。reduced motion では何もしない (CSS 側が opacity:1)。

const Y = 24;
const DURATION = 0.8;
const EASE = 'power3.out';
const STAGGER = 0.08;
const START = 'top 90%';
// sequence: 前のコマが終わる少し手前で次を始め、順序は保ちつつ流れをつなぐ
const SEQ_OVERLAP = 0.45;

export function initReveal(root: ParentNode = document) {
  if (prefersReducedMotion) return;

  // --- 単体要素 ---
  // CSS で既に computed opacity:0 のため gsap.from は使えない (現在値=0 を
  // 終点と見なし 0→0 で消えたままになる)。fromTo で終点を明示する
  const singles = [...root.querySelectorAll<HTMLElement>('[data-reveal]')];
  for (const el of singles) {
    const fadeOnly = el.dataset.reveal === 'fade';
    const start = el.dataset.revealStart || START;
    gsap.fromTo(
      el,
      { autoAlpha: 0, y: fadeOnly ? 0 : Y },
      {
        autoAlpha: 1,
        y: 0,
        duration: DURATION,
        ease: EASE,
        clearProps: 'transform',
        scrollTrigger: { trigger: el, start, once: true },
      },
    );
  }

  // --- リスト (子要素を stagger) ---
  // batch でまとめて画面に入った子だけを stagger 表示する。背の高いグリッド
  // (stockists 等) でも、行が入るたびに順に出るので画面外で先に出きらない
  const groups = [...root.querySelectorAll<HTMLElement>('[data-reveal-group]')];
  for (const group of groups) {
    // sequence 配下のネストした群は、親の timeline がまとめて扱うのでスキップ
    if (group.parentElement?.closest('[data-reveal-group="sequence"]'))
      continue;

    const items = [...group.children] as HTMLElement[];
    if (!items.length) continue;
    // 間隔は data-reveal-stagger="0.1" (秒) で個別上書き可。未指定/不正は既定
    const attr = Number.parseFloat(group.dataset.revealStagger ?? '');
    const stagger = Number.isFinite(attr) ? attr : STAGGER;
    // 開始位置は data-reveal-start で個別上書き可
    const start = group.dataset.revealStart || START;
    // data-reveal-fade が付いていれば y 移動を省き opacity のみで登場
    const y = group.dataset.revealFade !== undefined ? 0 : Y;
    // 初期状態: opacity は CSS が 0。y だけ不可視のうちに下げておく
    gsap.set(items, { y });

    // "sequence": 群を 1 つの timeline にし、直下の子を宣言順に登場させる。
    // 子がさらに group なら、その子孫を stagger して 1 コマ分として差し込む
    if (group.dataset.revealGroup === 'sequence') {
      const tl = gsap.timeline({
        scrollTrigger: { trigger: group, start, once: true },
      });
      let at = 0;
      for (const child of items) {
        const nested = child.matches('[data-reveal-group]');
        const targets = nested
          ? ([...child.children] as HTMLElement[])
          : [child];
        if (!targets.length) continue;
        // 入れ子コンテナ自身は即表示にし (中身は CSS が伏せたまま)、y も戻す
        if (nested) gsap.set(child, { autoAlpha: 1, y: 0 });
        gsap.set(targets, { y });
        const sAttr = Number.parseFloat(child.dataset.revealStagger ?? '');
        const s = Number.isFinite(sAttr) ? sAttr : STAGGER;
        // data-reveal-lead="0.3" でこのコマだけ既定位置より早く始められる
        const lead = Number.parseFloat(child.dataset.revealLead ?? '');
        const pos = Math.max(0, at - (Number.isFinite(lead) ? lead : 0));
        tl.to(
          targets,
          {
            autoAlpha: 1,
            y: 0,
            duration: DURATION,
            ease: EASE,
            stagger: s,
            clearProps: 'transform',
          },
          pos,
        );
        at += DURATION + Math.max(0, targets.length - 1) * s - SEQ_OVERLAP;
      }
      continue;
    }

    // "together": グループ自体をトリガーにし、群が入ったら子をまとめて表示。
    // 子ごとに判定する batch と違い、群の上端が start に達した一度で全部出す
    if (group.dataset.revealGroup === 'together') {
      gsap.to(items, {
        autoAlpha: 1,
        y: 0,
        duration: DURATION,
        ease: EASE,
        stagger,
        clearProps: 'transform',
        scrollTrigger: { trigger: group, start, once: true },
      });
      continue;
    }

    ScrollTrigger.batch(items, {
      start,
      once: true,
      onEnter: (batch) =>
        gsap.to(batch, {
          autoAlpha: 1,
          y: 0,
          duration: DURATION,
          ease: EASE,
          stagger,
          clearProps: 'transform',
        }),
    });
  }

  // from / batch の初期状態が確定したのち、トリガー位置を取り直す
  // (画像・フォントのレイアウト確定ずれに追従)
  ScrollTrigger.refresh();
}
